import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import ChartDrawingOverlay from "./ChartDrawingOverlay";
import ChartWorkspaceCrosshairContext from "./ChartWorkspaceCrosshairContext";
import ChartWorkspaceDrawingContext from "./ChartWorkspaceDrawingContext";
import { translateDrawingPoints } from "./drawingEditing";
import { chartPointToSvg, svgCoordinateToChartPoint } from "./drawingGeometry";
import { canUseDrawingTool } from "./drawingModel";
import {
  addDrawingPlacementPoint,
  beginDrawingPlacement,
  createDrawingPlacementState,
} from "./drawingPlacement";

function pointerToSvgCoordinate(event) {
  const svg = event.currentTarget.ownerSVGElement;
  if (!svg || typeof svg.getBoundingClientRect !== "function") return null;

  const rect = svg.getBoundingClientRect();
  const viewBox = svg.viewBox?.baseVal;
  if (!rect.width || !rect.height || !viewBox?.width || !viewBox?.height) return null;

  return {
    x: viewBox.x + ((event.clientX - rect.left) / rect.width) * viewBox.width,
    y: viewBox.y + ((event.clientY - rect.top) / rect.height) * viewBox.height,
  };
}

function formatCrosshairTime(value) {
  if (!value) return "Time unavailable";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Time unavailable";

  return parsed.toLocaleTimeString("en-US", {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatCrosshairPrice(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `$${number.toFixed(2)}` : "Price unavailable";
}

function ChartDrawingInteractionLayer({
  drawings = [],
  selectedToolId = null,
  placementContextKey = "",
  geometry,
  chartWidth,
  priceHeight,
  onCreateDrawing,
  onSelectDrawing,
}) {
  const [placement, setPlacement] = useState(createDrawingPlacementState);
  const crosshairContext = useContext(ChartWorkspaceCrosshairContext);
  const drawingContext = useContext(ChartWorkspaceDrawingContext);
  const dragState = useRef(null);
  const drawingToolActive = canUseDrawingTool(selectedToolId);
  const crosshairActive = Boolean(crosshairContext?.enabled);
  const interactionActive = drawingToolActive || crosshairActive;
  const effectivePlacementContextKey = useMemo(() => {
    if (placementContextKey) return placementContextKey;
    const candles = Array.isArray(geometry?.candles) ? geometry.candles : [];
    const first = candles[0]?.timestamp || "";
    const last = candles[candles.length - 1]?.timestamp || "";
    return `${first}|${last}|${candles.length}`;
  }, [geometry?.candles, placementContextKey]);
  const crosshairSvgPoint = useMemo(() => {
    if (!crosshairActive || !crosshairContext?.point) return null;
    return chartPointToSvg(
      crosshairContext.point,
      geometry?.candles || [],
      geometry?.priceToY
    );
  }, [crosshairActive, crosshairContext?.point, geometry?.candles, geometry?.priceToY]);

  useEffect(() => {
    setPlacement(
      drawingToolActive
        ? beginDrawingPlacement(selectedToolId)
        : createDrawingPlacementState()
    );
    dragState.current = null;
  }, [drawingToolActive, effectivePlacementContextKey, selectedToolId]);

  const handlePointerDown = (event) => {
    if (!drawingToolActive) return;
    const svgCoordinate = pointerToSvgCoordinate(event);
    if (!svgCoordinate) return;

    const point = svgCoordinateToChartPoint(svgCoordinate, geometry, priceHeight);
    if (!point) return;

    event.stopPropagation();
    if (event.cancelable) event.preventDefault();

    const result = addDrawingPlacementPoint(placement, point);
    setPlacement(result.state);

    if (result.completedPoints) {
      onCreateDrawing?.({
        toolId: selectedToolId,
        points: result.completedPoints,
      });
    }
  };

  const handleSurfacePointerMove = (event) => {
    if (!crosshairActive || typeof crosshairContext?.setPoint !== "function") return;
    const svgCoordinate = pointerToSvgCoordinate(event);
    if (!svgCoordinate) return;
    const point = svgCoordinateToChartPoint(svgCoordinate, geometry, priceHeight);
    if (point) crosshairContext.setPoint(point);
  };

  const handleSurfacePointerLeave = () => {
    if (crosshairActive && typeof crosshairContext?.setPoint === "function") {
      crosshairContext.setPoint(null);
    }
  };

  const handleDrawingPointerDown = (event, drawing) => {
    onSelectDrawing?.(drawing.id);
    if (drawing.isLocked || typeof drawingContext?.updateDrawing !== "function") return;

    const svgCoordinate = pointerToSvgCoordinate(event);
    const origin = svgCoordinateToChartPoint(svgCoordinate || {}, geometry, priceHeight);
    if (!origin) return;

    if (event.cancelable) event.preventDefault();
    if (typeof event.currentTarget.setPointerCapture === "function") {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (error) {
        // Pointer capture improves drag continuity but is not required for updates.
      }
    }

    dragState.current = {
      drawing,
      drawingId: drawing.id,
      origin,
      pointerId: event.pointerId,
    };
  };

  const handleDrawingPointerMove = (event, drawing) => {
    const drag = dragState.current;
    if (!drag || drag.drawingId !== drawing.id || drag.pointerId !== event.pointerId) return;

    const svgCoordinate = pointerToSvgCoordinate(event);
    const currentPoint = svgCoordinateToChartPoint(svgCoordinate || {}, geometry, priceHeight);
    if (!currentPoint) return;

    const points = translateDrawingPoints(
      drag.drawing,
      drag.origin,
      currentPoint,
      geometry?.candles
    );
    drawingContext.updateDrawing(drawing.id, { points });
    if (event.cancelable) event.preventDefault();
  };

  const handleDrawingPointerUp = (event, drawing) => {
    const drag = dragState.current;
    if (!drag || drag.drawingId !== drawing.id) return;

    if (typeof event.currentTarget.releasePointerCapture === "function") {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId);
      } catch (error) {
        // The browser may already have released pointer capture.
      }
    }
    dragState.current = null;
  };

  return (
    <g data-testid="chart-drawing-interaction-layer">
      <rect
        data-testid="chart-drawing-hit-area"
        x="0"
        y="8"
        width={chartWidth}
        height={priceHeight}
        fill="transparent"
        pointerEvents={interactionActive ? "all" : "none"}
        aria-hidden="true"
        onPointerDown={handlePointerDown}
        onPointerMove={handleSurfacePointerMove}
        onPointerLeave={handleSurfacePointerLeave}
      />
      {crosshairSvgPoint ? (
        <g data-testid="chart-linked-crosshair" pointerEvents="none">
          <line
            data-testid="chart-linked-crosshair-vertical"
            className="chartLinkedCrosshairLine"
            x1={crosshairSvgPoint.x}
            x2={crosshairSvgPoint.x}
            y1="8"
            y2={priceHeight + 8}
          />
          <line
            data-testid="chart-linked-crosshair-horizontal"
            className="chartLinkedCrosshairLine"
            x1="0"
            x2={chartWidth}
            y1={crosshairSvgPoint.y}
            y2={crosshairSvgPoint.y}
          />

          <g
            data-testid="chart-linked-crosshair-time-label"
            className="chartLinkedCrosshairLabel"
          >
            <rect
              x={Math.max(4, Math.min(chartWidth - 92, crosshairSvgPoint.x - 46))}
              y={priceHeight + 82}
              width="92"
              height="24"
              rx="5"
            />
            <text
              x={Math.max(50, Math.min(chartWidth - 46, crosshairSvgPoint.x))}
              y={priceHeight + 99}
              textAnchor="middle"
            >
              {formatCrosshairTime(crosshairContext.point?.timestamp)}
            </text>
          </g>

          <g
            data-testid="chart-linked-crosshair-price-label"
            className="chartLinkedCrosshairLabel"
          >
            <rect
              x={chartWidth - 84}
              y={Math.max(10, Math.min(priceHeight - 14, crosshairSvgPoint.y - 12))}
              width="80"
              height="24"
              rx="5"
            />
            <text
              x={chartWidth - 44}
              y={Math.max(27, Math.min(priceHeight + 3, crosshairSvgPoint.y + 5))}
              textAnchor="middle"
            >
              {formatCrosshairPrice(crosshairContext.point?.price)}
            </text>
          </g>
        </g>
      ) : null}
      <ChartDrawingOverlay
        drawings={drawings}
        geometry={geometry}
        chartWidth={chartWidth}
        priceHeight={priceHeight}
        onSelectDrawing={onSelectDrawing}
        onDrawingPointerDown={handleDrawingPointerDown}
        onDrawingPointerMove={handleDrawingPointerMove}
        onDrawingPointerUp={handleDrawingPointerUp}
      />
      {drawingToolActive && placement.points.length > 0 ? (
        <text
          data-testid="chart-drawing-placement-status"
          x="12"
          y="26"
          className="chartDrawingPlacementStatus"
          pointerEvents="none"
        >
          Anchor {placement.points.length} placed
        </text>
      ) : null}
    </g>
  );
}

export default ChartDrawingInteractionLayer;
